import React, { useState, useEffect } from 'react';
import { ArrowLeft, Save, AlertCircle, CheckCircle, Building2, MapPin, Calendar, Loader2, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import { 
  getTemplateTree, 
  setupMilestonesForSite,     // ✅ ADD: Import API function
  getMilestoneTreeBySite,     // ✅ ADD: For edit mode
  updateMilestoneBobot,       // ✅ ADD: For edit mode
  updateMilestoneDates,       // ✅ ADD: For edit mode
  getEvidenceDefinitions 
} from '../../services/milestoneService';

/**
 * TowerMilestoneForm - INTEGRATED VERSION
 * 
 * Features:
 * - Load milestone template from backend API
 * - Display hierarchical milestone structure
 * - Allow customization of bobot, dates, volume
 * - Submit to backend for milestone setup (CREATE MODE)
 * - Update existing milestones (EDIT MODE)
 * - Validation: Total bobot must = 100%
 * 
 * API Integration:
 * - GET /api/milestones/templates/tree/TOWER - Load template structure
 * - POST /api/sites/{id}/milestones/setup - Setup milestones (CREATE)
 * - GET /api/sites/{id}/milestones/tree - Load existing (EDIT)
 * - PUT /api/milestones/{id}/bobot - Update bobot (EDIT)
 * - PUT /api/milestones/{id}/dates - Update dates (EDIT)
 */
const TowerMilestoneForm = ({ site, project, onBack, onSave }) => {
  const [milestones, setMilestones] = useState([]);
  const [templateTree, setTemplateTree] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [mode, setMode] = useState('create'); // ✅ ADD: 'create' or 'edit'

  // ✅ Load milestone data on mount
  useEffect(() => {
    loadMilestoneData();
  }, [site.id]);

  /**
   * ✅ NEW: Load milestone data
   * Check if site already has milestones (edit mode)
   * If not, load from template (create mode)
   */
  const loadMilestoneData = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // First, check if site already has milestones
      const existingMilestones = await getMilestoneTreeBySite(site.id);

      if (existingMilestones && existingMilestones.length > 0) {
        // EDIT MODE: Load existing milestones
        console.log('📝 Edit mode: Loading existing milestones');
        setMode('edit');
        
        // Convert ProjectMilestone to template format for display
        const convertedMilestones = convertProjectMilestonesToTemplate(existingMilestones);
        setMilestones(convertedMilestones);
        
        toast.success('Loaded existing milestones for editing');
      } else {
        // CREATE MODE: Load from template
        console.log('✨ Create mode: Loading from template');
        setMode('create');
        
        const tree = await getTemplateTree('TOWER');
        setTemplateTree(tree);
        
        // Transform API response to form format
        const transformedMilestones = transformTemplate(tree);
        
        // Load evidence definitions with hierarchy
        await loadEvidenceForMilestones(transformedMilestones, tree);
        
        setMilestones(transformedMilestones);
        
        toast.success('Loaded milestone template');
      }
    } catch (err) {
      console.error('❌ Error loading milestone data:', err);
      setError('Failed to load milestone data');
      toast.error('Failed to load milestone data');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * ✅ NEW: Convert ProjectMilestone tree to template format for editing
   */
  const convertProjectMilestonesToTemplate = (projectMilestones) => {
    console.log('📥 Converting', projectMilestones.length, 'milestone trees');
    
    const convertMilestone = (pm) => {
      const milestone = {
        // Template fields
        templateId: pm.templateId,
        id: pm.id,
        itemCode: pm.itemCode,
        name: pm.milestoneName,
        level: pm.level,
        isGroup: pm.isGroup,
        
        // Evidence info
        evidence: pm.evidenceDescription || '',
        evidenceDefinitions: pm.evidenceDefinitions || [],
        jumlahEvidence: pm.evidenceDefinitions?.length > 0 
          ? (pm.evidenceDefinitions[0].minFileCount || 'n') 
          : 'n',
        
        // Bobot fields
        bobotDefault: pm.defaultBobot || 0,
        bobot: pm.effectiveBobot || pm.defaultBobot || 0,
        originalBobot: pm.effectiveBobot || pm.defaultBobot || 0,
        customBobot: pm.overriddenBobot,
        
        // Calculation method
        calculationMethod: pm.calculationMethod,
        volumeUnit: pm.volumeUnit,
        
        // Date fields
        planStartDate: pm.planStartDate || '',
        planFinishDate: pm.planFinishDate || '',
        
        // Parent reference
        parentId: pm.parentId,
        
        // Children
        children: []
      };
      
      // Recursively convert children
      if (pm.children && pm.children.length > 0) {
        milestone.children = pm.children.map(convertMilestone);
      }
      
      return milestone;
    };
    
    return projectMilestones.map(convertMilestone);
  };

  /**
   * Load evidence definitions with hierarchy for all milestones
   */
  const loadEvidenceForMilestones = async (milestones, templateTree) => {
    for (const milestone of milestones) {
      try {
        const templateId = findTemplateIdByCode(templateTree, milestone.id);
        
        if (templateId) {
          const evidenceList = await getEvidenceDefinitions(templateId);
          milestone.evidenceDefinitions = evidenceList;
        }
      } catch (error) {
        console.warn(`Failed to load evidence for milestone ${milestone.id}:`, error);
        milestone.evidenceDefinitions = milestone.evidenceDefinitions || [];
      }
      
      // Recursively load for children
      if (milestone.children && milestone.children.length > 0) {
        await loadEvidenceForMilestones(milestone.children, templateTree);
      }
    }
  };

  /**
   * Helper: Find template ID by item code recursively
   */
  const findTemplateIdByCode = (templates, itemCode) => {
    for (const template of templates) {
      if (template.itemCode === itemCode) {
        return template.id;
      }
      if (template.children && template.children.length > 0) {
        const found = findTemplateIdByCode(template.children, itemCode);
        if (found) return found;
      }
    }
    return null;
  };

  /**
   * Transform API template response to form format
   */
  const transformTemplate = (templates) => {
    return templates.map(template => transformNode(template));
  };

  /**
   * Recursively transform template node to form node
   */
  const transformNode = (template) => {
    const node = {
      id: template.itemCode,
      name: template.milestoneName,
      evidence: template.evidenceDescription || '',
      evidenceDefinitions: template.evidenceDefinitions || [],
      jumlahEvidence: '',
      bobotDefault: template.defaultBobot || 0,
      bobot: template.defaultBobot || 0,
      customBobot: null,
      planStartDate: '',
      planFinishDate: '',
      isGroup: template.isGroup,
      calculationMethod: template.calculationMethod,
      volumeUnit: template.volumeUnit,
      level: template.level,
      templateId: template.id, // ✅ ADD: Store template ID for CREATE mode
      children: []
    };

    if (template.children && template.children.length > 0) {
      node.children = template.children.map(child => transformNode(child));
    }

    return node;
  };

  const handleBobotChange = (id, value) => {
    const updateMilestone = (items) => {
      return items.map(item => {
        if (item.id === id) {
          const newBobot = parseFloat(value) || 0;
          const originalBobot = parseFloat(item.originalBobot || item.bobot) || 0;
          
          return { 
            ...item,
            bobot: newBobot,
            customBobot: newBobot // ✅ Always set for change detection
          };
        }
        if (item.children && item.children.length > 0) {
          return { ...item, children: updateMilestone(item.children) };
        }
        return item;
      });
    };
    setMilestones(updateMilestone(milestones));
  };

  const handleJumlahEvidenceChange = (id, value) => {
    const updateMilestone = (items) => {
      return items.map(item => {
        if (item.id === id) {
          return { ...item, jumlahEvidence: value };
        }
        if (item.children && item.children.length > 0) {
          return { ...item, children: updateMilestone(item.children) };
        }
        return item;
      });
    };
    setMilestones(updateMilestone(milestones));
  };

  const handleDateChange = (id, field, value) => {
    const updateMilestone = (items) => {
      return items.map(item => {
        if (item.id === id) {
          return { ...item, [field]: value };
        }
        if (item.children && item.children.length > 0) {
          return { ...item, children: updateMilestone(item.children) };
        }
        return item;
      });
    };
    setMilestones(updateMilestone(milestones));
  };

  const calculateTotal = () => {
    let total = 0;
    const calcBobot = (items) => {
      items.forEach(item => {
        if (!item.children || item.children.length === 0) {
          total += item.bobot || 0;
        } else {
          calcBobot(item.children);
        }
      });
    };
    calcBobot(milestones);
    return total;
  };

  /**
   * ✅ NEW: Build customizations array for API request (CREATE mode)
   */
  const buildCustomizations = () => {
    const customizations = [];

    const collectCustomizations = (items) => {
      items.forEach(item => {
        // Only include if there are customizations
        const hasCustomBobot = item.customBobot !== null && item.customBobot !== item.bobotDefault;
        const hasCustomDates = item.planStartDate || item.planFinishDate;

        if (hasCustomBobot || hasCustomDates) {
          customizations.push({
            templateId: item.templateId, // ✅ Use templateId from template
            customBobot: hasCustomBobot ? item.customBobot : null,
            bobotOverrideReason: hasCustomBobot ? 'Adjusted by PM' : null,
            planStartDate: item.planStartDate || null,
            planFinishDate: item.planFinishDate || null
          });
        }

        // Recurse children
        if (item.children && item.children.length > 0) {
          collectCustomizations(item.children);
        }
      });
    };

    collectCustomizations(milestones);
    return customizations;
  };

  /**
   * ✅ UPDATED: Handle form submit with proper API integration
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate total bobot
    const total = calculateTotal();
    if (Math.abs(total - 100) > 0.01) {
      toast.error(`Total bobot harus 100%. Saat ini: ${total.toFixed(2)}%`);
      return;
    }

    setIsSubmitting(true);

    try {
      if (mode === 'create') {
        // ✅ CREATE MODE: Setup new milestones
        const customizations = buildCustomizations();

        const request = {
          siteId: site.id,
          projectType: 'TOWER',
          milestoneCustomizations: customizations
        };

        console.log('📤 Submitting tower milestone setup:', request);

        // ✅ CALL API: setupMilestonesForSite
        await setupMilestonesForSite(site.id, request);
        
        toast.success('✅ Milestone setup berhasil!');
        
        if (onSave) {
          await onSave(site.id);
        }
      } else {
        // ✅ EDIT MODE: Update existing milestones
        console.log('📝 Edit mode - updating milestones');
        
        // Collect all milestones into flat list
        const allMilestones = [];
        const collectMilestones = (items) => {
          items.forEach(item => {
            allMilestones.push(item);
            if (item.children && item.children.length > 0) {
              collectMilestones(item.children);
            }
          });
        };
        collectMilestones(milestones);
        
        // Track changes
        const updates = {
          bobot: [],
          dates: []
        };
        
        // Compare and collect changes
        allMilestones.forEach((milestone) => {
          if (!milestone.id || milestone.isGroup) {
            return;
          }
          
          // Check bobot change
          if (milestone.customBobot !== undefined && milestone.customBobot !== null) {
            const newBobot = parseFloat(milestone.customBobot);
            const originalBobot = parseFloat(milestone.originalBobot || 0);
            
            if (newBobot !== originalBobot && newBobot > 0 && !isNaN(newBobot)) {
              updates.bobot.push({
                id: milestone.id,
                data: {
                  milestoneId: milestone.id,
                  newBobot: newBobot,
                  reason: 'Adjusted by PM'
                }
              });
            }
          }
          
          // Check dates change
          if (!milestone.isGroup) {
            const hasStartDate = milestone.planStartDate && milestone.planStartDate.trim() !== '';
            const hasFinishDate = milestone.planFinishDate && milestone.planFinishDate.trim() !== '';
            
            if (hasStartDate || hasFinishDate) {
              updates.dates.push({
                id: milestone.id,
                data: {
                  milestoneId: milestone.id,
                  planStartDate: hasStartDate ? milestone.planStartDate : null,
                  planFinishDate: hasFinishDate ? milestone.planFinishDate : null
                }
              });
            }
          }
        });
        
        console.log('📊 Updates to apply:', {
          bobotUpdates: updates.bobot.length,
          datesUpdates: updates.dates.length
        });
        
        // Apply updates
        let successCount = 0;
        let errorCount = 0;
        
        // Update bobots
        for (const update of updates.bobot) {
          try {
            await updateMilestoneBobot(update.id, update.data);
            successCount++;
          } catch (err) {
            console.error(`Failed to update bobot for milestone ${update.id}:`, err);
            errorCount++;
          }
        }
        
        // Update dates
        for (const update of updates.dates) {
          try {
            await updateMilestoneDates(update.id, update.data);
            successCount++;
          } catch (err) {
            console.error(`Failed to update dates for milestone ${update.id}:`, err);
            errorCount++;
          }
        }
        
        if (errorCount > 0) {
          toast.error(`Sebagian milestone gagal diupdate`);
        } else if (successCount > 0) {
          toast.success(`✅ ${successCount} milestone berhasil diupdate!`);
        } else {
          toast.success('ℹ️ Tidak ada perubahan untuk disimpan');
        }
        
        if (onSave) {
          await onSave(site.id);
        }
      }
    } catch (err) {
      console.error('❌ Error saving milestone:', err);
      toast.error(err.response?.data?.message || 'Failed to save milestone');
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Build hierarchical evidence tree from flat list
   */
  const buildEvidenceTree = (flatList) => {
    if (!flatList || flatList.length === 0) {
      return [];
    }

    const evidenceMap = new Map();
    const rootNodes = [];

    flatList.forEach(evidence => {
      evidenceMap.set(evidence.id, {
        ...evidence,
        children: []
      });

      if (!evidence.parentId) {
        rootNodes.push(evidence.id);
      }
    });

    flatList.forEach(evidence => {
      if (evidence.parentId) {
        const parent = evidenceMap.get(evidence.parentId);
        const child = evidenceMap.get(evidence.id);
        
        if (parent && child) {
          parent.children.push(child);
        }
      }
    });

    evidenceMap.forEach(evidence => {
      if (evidence.children && evidence.children.length > 0) {
        evidence.children.sort((a, b) => 
          (a.displayOrder || 0) - (b.displayOrder || 0)
        );
      }
    });

    const tree = rootNodes
      .map(id => evidenceMap.get(id))
      .filter(Boolean)
      .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));

    return tree;
  };

  /**
   * Render evidence tree recursively with hierarchical numbering
   */
  const renderEvidenceTree = (evidenceList, level = 0, parentNumber = '') => {
    if (!evidenceList || evidenceList.length === 0) {
      return null;
    }

    return evidenceList.map((ev, idx) => {
      const indentPx = level * 20;
      const hasChildren = ev.children && ev.children.length > 0;
      
      const currentNumber = parentNumber 
        ? `${parentNumber}.${idx + 1}` 
        : `${idx + 1}`;

      return (
        <React.Fragment key={ev.id || idx}>
          <div 
            className={`text-xs border-l-2 transition-all ${
              ev.isGroup 
                ? 'p-3 bg-yellow-50 border-yellow-400 rounded-md' 
                : 'p-2 bg-white border-gray-300 rounded-md hover:bg-gray-50'
            }`}
            style={{ marginLeft: `${indentPx}px` }}
          >
            <div className="flex items-start gap-2 mb-1">
              {ev.isGroup && (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-yellow-200 text-yellow-900 border border-yellow-400 flex-shrink-0">
                  📁 GROUP
                </span>
              )}
              
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-gray-500 min-w-[30px]">
                    {currentNumber}.
                  </span>
                  
                  <span className={`font-semibold ${
                    ev.isGroup ? 'text-yellow-900 text-sm' : 'text-gray-900'
                  }`}>
                    {ev.evidenceName}
                  </span>
                  
                  {ev.isMandatory && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-red-100 text-red-800 border border-red-300">
                      MANDATORY
                    </span>
                  )}
                  
                  {!ev.isMandatory && !ev.isGroup && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-blue-100 text-blue-800 border border-blue-300">
                      OPTIONAL
                    </span>
                  )}
                  
                  {(ev.level !== undefined && ev.level > 0) && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-purple-100 text-purple-700 border border-purple-300">
                      L{ev.level}
                    </span>
                  )}
                </div>
              </div>
            </div>
            
            {ev.isGroup && ev.description && (
              <div className="text-yellow-800 text-xs font-medium mt-1 flex items-center gap-1 ml-8">
                <span>📋</span>
                <span>{ev.description}</span>
              </div>
            )}
            
            {!ev.isGroup && ev.description && (
              <div className="text-gray-600 italic text-xs mt-1 border-l-2 border-gray-300 pl-2 ml-8">
                {ev.description}
              </div>
            )}
            
            {!ev.isGroup && (
              <div className="flex flex-wrap gap-3 text-xs text-gray-600 mt-2 ml-8">
                {ev.evidenceType && (
                  <span className="flex items-center gap-1">
                    <span className="font-medium text-gray-500">Type:</span>
                    <span className={`px-2 py-0.5 rounded font-semibold border ${
                      ev.evidenceType === 'PHOTO' ? 'bg-purple-100 text-purple-700 border-purple-200' :
                      ev.evidenceType === 'DOCUMENT' ? 'bg-blue-100 text-blue-700 border-blue-200' :
                      ev.evidenceType === 'VIDEO' ? 'bg-pink-100 text-pink-700 border-pink-200' :
                      'bg-gray-100 text-gray-700 border-gray-200'
                    }`}>
                      {ev.evidenceType}
                    </span>
                  </span>
                )}
                
                {(ev.minFileCount !== null || ev.maxFileCount !== null) && (
                  <span className="flex items-center gap-1">
                    <span className="font-medium text-gray-500">Files:</span>
                    <span className="font-semibold text-gray-800">
                      {ev.minFileCount || 0}-{ev.maxFileCount || '∞'}
                    </span>
                  </span>
                )}
                
                {ev.allowedFileTypes && (
                  <span className="flex items-center gap-1">
                    <span className="font-medium text-gray-500">Allowed:</span>
                    <span className="font-mono text-gray-700 text-xs">
                      {ev.allowedFileTypes}
                    </span>
                  </span>
                )}
                
                {ev.maxFileSizeMb && (
                  <span className="flex items-center gap-1">
                    <span className="font-medium text-gray-500">Max:</span>
                    <span className="font-semibold text-gray-800">
                      {ev.maxFileSizeMb} MB
                    </span>
                  </span>
                )}
              </div>
            )}
          </div>

          {hasChildren && (
            <div className="ml-2">
              {renderEvidenceTree(ev.children, level + 1, currentNumber)}
            </div>
          )}
        </React.Fragment>
      );
    });
  };

  const renderMilestone = (item, level = 0, parentNumber = '') => {
    const hasChildren = item.children && item.children.length > 0;
    const paddingLeft = level * 24;
    
    return (
      <React.Fragment key={item.id}>
        <tr className={`${
          level === 0 ? 'bg-blue-50' : 
          level === 1 ? 'bg-white' : 
          'bg-gray-50'
        } border-b border-gray-200 hover:bg-gray-100 transition-colors`}>
          <td className="px-4 py-3 text-sm" style={{ paddingLeft: `${paddingLeft + 16}px` }}>
            <span className={`${level === 0 ? 'font-bold text-gray-900' : 'text-gray-700'}`}>
              {item.id}.
            </span>
          </td>

          <td className="px-4 py-3 text-sm">
            <div className="flex items-center gap-2">
              {hasChildren && (
                <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded-full font-semibold">
                  GROUP
                </span>
              )}
              <span className={`${level === 0 ? 'font-bold text-gray-900' : 'text-gray-700'}`}>
                {item.name}
              </span>
            </div>
          </td>

          <td className="px-4 py-3 text-sm text-gray-600">
            {item.evidenceDefinitions && item.evidenceDefinitions.length > 0 ? (
              <div className="space-y-2">
                {(() => {
                  const tree = buildEvidenceTree(item.evidenceDefinitions);
                  return renderEvidenceTree(tree, 0);
                })()}
              </div>
            ) : (
              <div className="text-xs text-gray-400 italic">
                No evidence requirements
              </div>
            )}
          </td>

          <td className="px-4 py-3 text-sm">
            {!hasChildren && (
              <input
                type="text"
                value={item.jumlahEvidence}
                onChange={(e) => handleJumlahEvidenceChange(item.id, e.target.value)}
                className="w-20 px-2 py-1 border border-gray-300 rounded text-center focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="n"
                disabled={mode === 'edit'}
              />
            )}
          </td>

          <td className="px-4 py-3 text-sm">
            {!hasChildren ? (
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">{item.bobotDefault}%</span>
                <input
                  type="number"
                  step="0.01"
                  value={item.bobot}
                  onChange={(e) => handleBobotChange(item.id, e.target.value)}
                  className={`w-20 px-2 py-1 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-center ${
                    item.customBobot !== null ? 'border-orange-400 bg-orange-50' : 'border-gray-300'
                  }`}
                  min="0"
                  max="100"
                />
                <span className="text-gray-600">%</span>
              </div>
            ) : (
              <span className="font-semibold text-blue-700">{item.bobot}%</span>
            )}
          </td>

          <td className="px-4 py-3 text-sm">
            {!hasChildren && (
              <input
                type="date"
                value={item.planStartDate}
                onChange={(e) => handleDateChange(item.id, 'planStartDate', e.target.value)}
                className="w-40 px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs"
              />
            )}
          </td>

          <td className="px-4 py-3 text-sm">
            {!hasChildren && (
              <input
                type="date"
                value={item.planFinishDate}
                onChange={(e) => handleDateChange(item.id, 'planFinishDate', e.target.value)}
                className="w-40 px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs"
              />
            )}
          </td>
        </tr>
        {hasChildren && item.children.map(child => renderMilestone(child, level + 1, item.id))}
      </React.Fragment>
    );
  };

  const totalBobot = calculateTotal();

  // ========================================
  // RENDER: Loading State
  // ========================================
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading milestone template...</p>
        </div>
      </div>
    );
  }

  // ========================================
  // RENDER: Error State
  // ========================================
  if (error) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
          <div className="flex items-center gap-4">
            <button onClick={onBack} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <h1 className="text-2xl font-bold text-gray-800">Tower Site Milestone Planning</h1>
          </div>
        </div>

        <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-red-800 mb-2">{error}</h3>
              <p className="text-red-700 mb-4">Unable to load milestone data. Please try again.</p>
              <button
                onClick={loadMilestoneData}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ========================================
  // RENDER: Main Form
  // ========================================
  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
        <div className="flex items-center gap-4">
          <button 
            onClick={onBack} 
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-gray-800">
              {mode === 'create' ? '✨ Setup' : '📝 Edit'} Tower Site Milestones
            </h1>
            <p className="text-gray-600 mt-1">{site.siteName} - {project.projectCode}</p>
          </div>
          <div className="flex items-center gap-2">
            <Building2 className="w-6 h-6 text-blue-600" />
            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
              mode === 'create' 
                ? 'bg-green-100 text-green-800' 
                : 'bg-blue-100 text-blue-800'
            }`}>
              {mode === 'create' ? 'New Setup' : 'Edit Mode'}
            </span>
          </div>
        </div>
      </div>

      {/* Main Form Card */}
      <div className="bg-white rounded-lg shadow-md border border-gray-200">
        {/* Tower Site Profile Header */}
        <div className="p-6 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-t-lg">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
              <Building2 className="w-6 h-6" />
            </div>
            <div>
              <h3 className="text-lg font-semibold">Tower Site Profile</h3>
              <p className="text-blue-100 text-sm">Complete milestone planning details</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-blue-100 mb-1">Site ID</label>
              <p className="text-sm font-semibold">{site.siteId || 'ETJI0219'}</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-blue-100 mb-1">Site Name</label>
              <p className="text-sm font-semibold">{site.siteName}</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-blue-100 mb-1">Project Name</label>
              <p className="text-sm font-semibold">{project.projectName}</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-blue-100 mb-1">Regional</label>
              <p className="text-sm">{site.regional || 'JAWA TIMUR'}</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-blue-100 mb-1">Island</label>
              <p className="text-sm">{site.island || 'JAWA'}</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-blue-100 mb-1">Location</label>
              <p className="text-sm flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                {site.location}
              </p>
            </div>
            <div>
              <label className="block text-xs font-medium text-blue-100 mb-1">Category</label>
              <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-white bg-opacity-20">
                {site.category}
              </span>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="p-6">
            {/* Info Banner */}
            <div className="mb-4 p-4 bg-blue-50 border-l-4 border-blue-500 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-blue-800">Milestone Setup Instructions</p>
                  <ul className="text-sm text-blue-700 mt-2 space-y-1 list-disc list-inside">
                    <li>Default bobot shown in gray - adjust as needed</li>
                    <li>Orange background indicates customized bobot</li>
                    <li>Total bobot must equal 100%</li>
                    <li>Add planned start/finish dates for each milestone</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Milestone Table */}
            <div className="overflow-x-auto border border-gray-200 rounded-lg">
              <table className="w-full">
                <thead className="bg-gradient-to-r from-blue-600 to-blue-500 text-white">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider whitespace-nowrap">NO</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider whitespace-nowrap">MILESTONE</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider whitespace-nowrap w-96">
                      <div>EVIDENCE REQUIREMENTS</div>
                      <div className="text-xs font-normal opacity-75">(from template configuration)</div>
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider whitespace-nowrap">Jumlah Evidence</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider whitespace-nowrap">
                      <div>BOBOT</div>
                      <div className="text-xs font-normal opacity-75">(default / custom)</div>
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider whitespace-nowrap">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        Plan Start
                      </div>
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider whitespace-nowrap">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        Plan Finish
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {milestones.map(milestone => renderMilestone(milestone, 0))}
                  
                  {/* Total Row */}
                  <tr className="bg-gradient-to-r from-gray-100 to-gray-50 font-bold border-t-2 border-gray-300">
                    <td colSpan="4" className="px-4 py-3 text-right text-sm text-gray-700">TOTAL BOBOT:</td>
                    <td className="px-4 py-3 text-sm">
                      <div className="flex items-center gap-2">
                        {Math.abs(totalBobot - 100) < 0.01 ? (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        ) : (
                          <AlertCircle className="w-5 h-5 text-red-600" />
                        )}
                        <span className={`font-bold text-lg ${
                          Math.abs(totalBobot - 100) < 0.01 ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {totalBobot.toFixed(2)}%
                        </span>
                      </div>
                    </td>
                    <td colSpan="2"></td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Validation Warning/Success */}
            {Math.abs(totalBobot - 100) >= 0.01 ? (
              <div className="mt-4 p-4 bg-red-50 border-l-4 border-red-500 rounded-lg flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-red-800">Total Bobot Tidak Valid</p>
                  <p className="text-sm text-red-700 mt-1">
                    Total bobot harus 100%. Saat ini: <strong>{totalBobot.toFixed(2)}%</strong>. 
                    Silakan sesuaikan nilai bobot sebelum menyimpan.
                  </p>
                </div>
              </div>
            ) : (
              <div className="mt-4 p-4 bg-green-50 border-l-4 border-green-500 rounded-lg flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-green-800">Total Bobot Valid ✓</p>
                  <p className="text-sm text-green-700 mt-1">
                    Total bobot sudah mencapai 100%. Anda dapat menyimpan milestone ini.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-lg flex justify-end gap-3">
            <button
              type="button"
              onClick={onBack}
              className="px-6 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 font-medium transition-colors"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg transition-all"
              disabled={isSubmitting || Math.abs(totalBobot - 100) >= 0.01}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  {mode === 'create' ? 'Setup Milestone' : 'Update Milestone'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TowerMilestoneForm;